import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  TextInput,
  Select,
  SegmentedControl,
  Switch,
  Button,
  Text,
  Divider,
  Tabs,
  Paper,
  Alert,
} from '@mantine/core';
import { IconAppWindow, IconMaximize, IconInfoCircle } from '@tabler/icons-react';
import {
  ExtensionSlotConfig,
  ExtensionLayoutConfig,
  ExtensionDisplayMode,
  ExtensionResponsiveMode,
  ExtensionPosition,
  ExtensionLayoutMode,
  ExtensionAlignment,
} from '@/API';

interface ExtensionModalProps {
  opened: boolean;
  onClose: () => void;
  onSave: (extension: ExtensionSlotConfig) => void;
  initialValue?: ExtensionSlotConfig | null;
  availableBundles: string[];
}

const normalizePosition = (val?: unknown): ExtensionPosition => {
  const lower = String(val || '').toLowerCase();
  if (lower === 'right') return ExtensionPosition.Right;
  if (lower === 'top') return ExtensionPosition.Top;
  if (lower === 'bottom') return ExtensionPosition.Bottom;
  return ExtensionPosition.Left;
};

const normalizeLayoutMode = (val?: unknown): ExtensionLayoutMode => {
  const lower = String(val || '').toLowerCase();
  if (lower === 'windowpanel' || lower === 'window-panel') return ExtensionLayoutMode.WindowPanel;
  return ExtensionLayoutMode.FloatingDock;
};

const normalizeAlignment = (val?: unknown): ExtensionAlignment => {
  const lower = String(val || '').toLowerCase();
  if (lower === 'start') return ExtensionAlignment.Start;
  if (lower === 'center') return ExtensionAlignment.Center;
  if (lower === 'stretch') return ExtensionAlignment.Stretch;
  return ExtensionAlignment.End;
};

export function ExtensionModal({
  opened,
  onClose,
  onSave,
  initialValue,
  availableBundles,
}: ExtensionModalProps) {
  // Shared base settings
  const [activeTab, setActiveTab] = useState<string | null>('standard');
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [bundlePath, setBundlePath] = useState<string | null>(null);
  const [displayMode, setDisplayMode] = useState<ExtensionDisplayMode>(ExtensionDisplayMode.Docked);
  const [responsiveMode, setResponsiveMode] = useState<ExtensionResponsiveMode>(ExtensionResponsiveMode.Hide);

  // Standard window layout
  const [stdEnabled, setStdEnabled] = useState(true);
  const [stdPosition, setStdPosition] = useState<ExtensionPosition>(ExtensionPosition.Left);
  const [stdLayoutMode, setStdLayoutMode] = useState<ExtensionLayoutMode>(ExtensionLayoutMode.FloatingDock);
  const [stdWidth, setStdWidth] = useState('');
  const [stdHeight, setStdHeight] = useState('');
  const [stdAlignment, setStdAlignment] = useState<ExtensionAlignment>(ExtensionAlignment.End);

  // Fullscreen override layout
  const [hasFullscreenOverride, setHasFullscreenOverride] = useState(false);
  const [fsEnabled, setFsEnabled] = useState(true);
  const [fsPosition, setFsPosition] = useState<ExtensionPosition>(ExtensionPosition.Left);
  const [fsLayoutMode, setFsLayoutMode] = useState<ExtensionLayoutMode>(ExtensionLayoutMode.WindowPanel);
  const [fsWidth, setFsWidth] = useState('');
  const [fsHeight, setFsHeight] = useState('');
  const [fsAlignment, setFsAlignment] = useState<ExtensionAlignment>(ExtensionAlignment.Center);

  useEffect(() => {
    if (opened) {
      setActiveTab('standard');
      if (initialValue) {
        setId(initialValue.id || '');
        setName(initialValue.name || '');
        setBundlePath(initialValue.bundlePath || null);
        setDisplayMode(initialValue.displayMode || ExtensionDisplayMode.Docked);
        setResponsiveMode(initialValue.responsiveMode || ExtensionResponsiveMode.Hide);

        // Standard layout resolution
        const std = initialValue.standard;
        const resolvedStdEnabled = std?.enabled ?? true;
        const resolvedStdPos = normalizePosition(std?.position);
        const resolvedStdLayout = normalizeLayoutMode(std?.layoutMode);
        const resolvedStdWidth = std?.width || '';
        const resolvedStdHeight = std?.height || '';
        const resolvedStdAlign = normalizeAlignment(std?.alignment);

        setStdEnabled(resolvedStdEnabled);
        setStdPosition(resolvedStdPos);
        setStdLayoutMode(resolvedStdLayout);
        setStdWidth(resolvedStdWidth);
        setStdHeight(resolvedStdHeight);
        setStdAlignment(resolvedStdAlign);

        // Fullscreen layout resolution
        const fs = initialValue.fullscreen;
        const hasFs = !!fs;
        setHasFullscreenOverride(hasFs);

        if (fs) {
          setFsEnabled(fs.enabled ?? true);
          setFsPosition(fs.position ? normalizePosition(fs.position) : resolvedStdPos);
          setFsLayoutMode(fs.layoutMode ? normalizeLayoutMode(fs.layoutMode) : resolvedStdLayout);
          setFsWidth(fs.width || '');
          setFsHeight(fs.height || '');
          setFsAlignment(fs.alignment ? normalizeAlignment(fs.alignment) : resolvedStdAlign);
        } else {
          setFsEnabled(resolvedStdEnabled);
          setFsPosition(resolvedStdPos);
          setFsLayoutMode(resolvedStdLayout);
          setFsWidth('');
          setFsHeight('');
          setFsAlignment(resolvedStdAlign);
        }
      } else {
        // Defaults for new slot
        setId(`slot-${Date.now().toString(36).slice(-4)}`);
        setName('');
        setBundlePath(availableBundles[0] || null);
        setDisplayMode(ExtensionDisplayMode.Docked);
        setResponsiveMode(ExtensionResponsiveMode.Hide);

        setStdEnabled(true);
        setStdPosition(ExtensionPosition.Left);
        setStdLayoutMode(ExtensionLayoutMode.FloatingDock);
        setStdWidth('280px');
        setStdHeight('auto');
        setStdAlignment(ExtensionAlignment.End);

        setHasFullscreenOverride(false);
        setFsEnabled(true);
        setFsPosition(ExtensionPosition.Right);
        setFsLayoutMode(ExtensionLayoutMode.WindowPanel);
        setFsWidth('380px');
        setFsHeight('');
        setFsAlignment(ExtensionAlignment.Center);
      }
    }
  }, [opened, initialValue, availableBundles]);

  const handleSave = () => {
    const cleanId = id.trim() || `slot-${Date.now().toString(36).slice(-4)}`;

    const standardLayout: ExtensionLayoutConfig = {
      enabled: stdEnabled,
      position: stdPosition,
      layoutMode: stdLayoutMode,
      width: stdWidth.trim() || undefined,
      height: stdHeight.trim() || undefined,
      alignment: stdLayoutMode === ExtensionLayoutMode.WindowPanel ? ExtensionAlignment.Center : stdAlignment,
    };

    let fullscreenLayout: ExtensionLayoutConfig | null = null;
    if (hasFullscreenOverride) {
      fullscreenLayout = {
        enabled: fsEnabled,
        position: fsPosition,
        layoutMode: fsLayoutMode,
        width: fsWidth.trim() || undefined,
        height: fsHeight.trim() || undefined,
        alignment: fsLayoutMode === ExtensionLayoutMode.WindowPanel ? ExtensionAlignment.Center : fsAlignment,
      };
    }

    onSave({
      id: cleanId,
      name: name.trim() || undefined,
      bundlePath: bundlePath || null,
      displayMode,
      responsiveMode,
      order: initialValue?.order ?? 0,
      standard: standardLayout,
      fullscreen: fullscreenLayout,
    });
    onClose();
  };

  const isBundleMissing = Boolean(
    bundlePath && !availableBundles.includes(bundlePath.split('/')[0])
  );

  const bundleSelectData = availableBundles.map((b) => ({ value: b, label: b }));
  if (isBundleMissing && bundlePath) {
    bundleSelectData.unshift({
      value: bundlePath,
      label: `${bundlePath} (資料不存在)`,
    });
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{initialValue ? '編輯擴充掛載插槽 (Extension Slot)' : '新增擴充掛載插槽 (Extension Slot)'}</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        <Group grow>
          <TextInput
            label="Slot 識別碼 (ID / Slug)"
            description="外部 Web Component 可用 slot={`extension-${id}`} 覆寫"
            placeholder="例如 mascot, side-panel"
            required
            value={id}
            onChange={(e) => setId(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_'))}
          />
          <TextInput
            label="擴充插槽名稱 (Name)"
            description="用於後台辨識"
            placeholder="例如 側邊欄、產品資訊、互動看板"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </Group>

        <Select
          label="關聯資產包 (Bundle Folder)"
          placeholder="選擇已上傳的資產包資料夾"
          data={bundleSelectData}
          value={bundlePath}
          onChange={(val) => setBundlePath(val)}
          clearable
          error={isBundleMissing ? '此關聯的資產包在儲存庫中已不存在' : undefined}
          description={
            isBundleMissing
              ? '警告：原設定的資產包已不存在，請重新選擇資產包或清空此欄位'
              : '聊天元件將自動加載該資產包內的 extension.json 描述檔'
          }
        />

        <Group grow>
          <div>
            <Text size="xs" fw={500} mb={4}>
              顯示模式 (Display Mode)
            </Text>
            <SegmentedControl
              fullWidth
              size="xs"
              value={displayMode}
              onChange={(val: any) => setDisplayMode(val)}
              data={[
                { label: '開啟時顯示 (Docked)', value: ExtensionDisplayMode.Docked },
                { label: '始終常駐 (Persistent)', value: ExtensionDisplayMode.Persistent },
              ]}
            />
          </div>

          <div>
            <Text size="xs" fw={500} mb={4}>
              行動端適應 (Responsive)
            </Text>
            <SegmentedControl
              fullWidth
              size="xs"
              value={responsiveMode}
              onChange={(val: any) => setResponsiveMode(val)}
              data={[
                { label: '小螢幕隱藏 (Hide)', value: ExtensionResponsiveMode.Hide },
                { label: '垂直堆疊 (Stack)', value: ExtensionResponsiveMode.Stack },
              ]}
            />
          </div>
        </Group>

        <Divider my="xs" />

        <Tabs value={activeTab} onChange={setActiveTab} variant="outline" radius="sm">
          <Tabs.List>
            <Tabs.Tab value="standard" leftSection={<IconAppWindow size={16} />}>
              一般視窗設定 (Standard)
            </Tabs.Tab>
            <Tabs.Tab value="fullscreen" leftSection={<IconMaximize size={16} />}>
              全螢幕設定 (Fullscreen)
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="standard" pt="md">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>
                  一般視窗排版設定
                </Text>
                <Switch
                  label="在一般視窗顯示"
                  checked={stdEnabled}
                  onChange={(e) => setStdEnabled(e.currentTarget.checked)}
                />
              </Group>

              <div>
                <Text size="xs" fw={500} mb={4}>
                  佈局架構模式 (Layout Mode)
                </Text>
                <SegmentedControl
                  fullWidth
                  size="xs"
                  value={stdLayoutMode}
                  onChange={(val: any) => setStdLayoutMode(val as ExtensionLayoutMode)}
                  data={[
                    { label: '外部浮動槽 (Floating Dock)', value: ExtensionLayoutMode.FloatingDock },
                    { label: '視窗延伸面板 (Window Panel)', value: ExtensionLayoutMode.WindowPanel },
                  ]}
                />
                <Text size="xs" c="dimmed" mt={4}>
                  {stdLayoutMode === ExtensionLayoutMode.WindowPanel
                    ? '視窗延伸面板：直接整合在聊天室窗體內（Header 橫跨頂部、左/右側邊分欄）。'
                    : '外部浮動槽：懸浮在聊天室外圍四周。'}
                </Text>
              </div>

              <div>
                <Text size="xs" fw={500} mb={4}>
                  附著方位 (Position)
                </Text>
                <SegmentedControl
                  fullWidth
                  size="xs"
                  value={stdPosition}
                  onChange={(val: any) => setStdPosition(val as ExtensionPosition)}
                  data={[
                    { label: '頂部 (Top)', value: ExtensionPosition.Top },
                    { label: '底部 (Bottom)', value: ExtensionPosition.Bottom },
                    { label: '左側 (Left)', value: ExtensionPosition.Left },
                    { label: '右側 (Right)', value: ExtensionPosition.Right },
                  ]}
                />
              </div>

              <Group grow align="flex-start">
                <TextInput
                  label="寬度 (Width)"
                  placeholder="例如 280px, auto"
                  description="一般視窗模式下的寬度（側邊欄預設 260px）"
                  value={stdWidth}
                  onChange={(e) => setStdWidth(e.currentTarget.value)}
                />
                <TextInput
                  label="高度 (Height)"
                  placeholder="例如 auto, 300px"
                  description="一般視窗模式下的高度"
                  value={stdHeight}
                  onChange={(e) => setStdHeight(e.currentTarget.value)}
                />
              </Group>

              {stdLayoutMode === ExtensionLayoutMode.FloatingDock && (
                <div>
                  <Text size="xs" fw={500} mb={4}>
                    對齊方式 (Alignment - {stdPosition === ExtensionPosition.Left || stdPosition === ExtensionPosition.Right ? '垂直對齊' : '水平對齊'})
                  </Text>
                  <SegmentedControl
                    fullWidth
                    size="xs"
                    value={stdAlignment}
                    onChange={(val: any) => setStdAlignment(val as ExtensionAlignment)}
                    data={[
                      { label: '頂部 / 靠左 (Start)', value: ExtensionAlignment.Start },
                      { label: '居中 (Center)', value: ExtensionAlignment.Center },
                      { label: '底部 / 靠右 (End)', value: ExtensionAlignment.End },
                      { label: '拉伸撐滿 (Stretch)', value: ExtensionAlignment.Stretch },
                    ]}
                  />
                </div>
              )}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="fullscreen" pt="md">
            <Stack gap="md">
              <Paper withBorder p="sm" radius="sm" bg={hasFullscreenOverride ? undefined : 'gray.0'}>
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm" fw={600}>
                      自訂全螢幕專屬排版
                    </Text>
                    <Text size="xs" c="dimmed">
                      開啟後可單獨指定全螢幕下的佈局模式、方位、尺寸或啟用狀態
                    </Text>
                  </div>
                  <Switch
                    size="md"
                    checked={hasFullscreenOverride}
                    onChange={(e) => setHasFullscreenOverride(e.currentTarget.checked)}
                  />
                </Group>
              </Paper>

              {!hasFullscreenOverride ? (
                <Alert icon={<IconInfoCircle size={16} />} title="繼承一般設定" color="blue" variant="light">
                  目前未自訂全螢幕排版。切換至全螢幕時，將直接沿用「一般視窗設定」的所有佈局方位與尺寸。
                </Alert>
              ) : (
                <Stack gap="md">
                  <Group justify="space-between" align="center">
                    <Text size="sm" fw={600}>
                      全螢幕排版設定
                    </Text>
                    <Switch
                      label="在全螢幕模式顯示"
                      checked={fsEnabled}
                      onChange={(e) => setFsEnabled(e.currentTarget.checked)}
                    />
                  </Group>

                  <div>
                    <Text size="xs" fw={500} mb={4}>
                      全螢幕佈局架構模式 (Layout Mode)
                    </Text>
                    <SegmentedControl
                      fullWidth
                      size="xs"
                      value={fsLayoutMode}
                      onChange={(val: any) => setFsLayoutMode(val as ExtensionLayoutMode)}
                      data={[
                        { label: '視窗延伸面板 (Window Panel - 內嵌分欄)', value: ExtensionLayoutMode.WindowPanel },
                        { label: '外部浮動槽 (Floating Dock - 懸浮掛件)', value: ExtensionLayoutMode.FloatingDock },
                      ]}
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                      {fsLayoutMode === ExtensionLayoutMode.WindowPanel
                        ? '視窗延伸面板：在全螢幕視窗內建立獨立分欄，並自動與聊天主區並排。'
                        : '外部浮動槽：懸浮在全螢幕視窗四邊上方。'}
                    </Text>
                  </div>

                  <div>
                    <Text size="xs" fw={500} mb={4}>
                      全螢幕附著方位 (Position)
                    </Text>
                    <SegmentedControl
                      fullWidth
                      size="xs"
                      value={fsPosition}
                      onChange={(val: any) => setFsPosition(val as ExtensionPosition)}
                      data={[
                        { label: '頂部 (Top)', value: ExtensionPosition.Top },
                        { label: '底部 (Bottom)', value: ExtensionPosition.Bottom },
                        { label: '左側 (Left)', value: ExtensionPosition.Left },
                        { label: '右側 (Right)', value: ExtensionPosition.Right },
                      ]}
                    />
                  </div>

                  <Group grow align="flex-start">
                    <TextInput
                      label="全螢幕寬度 (Width)"
                      placeholder="例如 380px, 30%, auto"
                      description="全螢幕模式下的寬度（留空則沿用一般寬度）"
                      value={fsWidth}
                      onChange={(e) => setFsWidth(e.currentTarget.value)}
                    />
                    <TextInput
                      label="全螢幕高度 (Height)"
                      placeholder="例如 auto, 30vh"
                      description="全螢幕模式下的高度（留空則沿用一般高度）"
                      value={fsHeight}
                      onChange={(e) => setFsHeight(e.currentTarget.value)}
                    />
                  </Group>

                  {fsLayoutMode === ExtensionLayoutMode.FloatingDock && (
                    <div>
                      <Text size="xs" fw={500} mb={4}>
                        對齊方式 (Alignment - {fsPosition === ExtensionPosition.Left || fsPosition === ExtensionPosition.Right ? '垂直對齊' : '水平對齊'})
                      </Text>
                      <SegmentedControl
                        fullWidth
                        size="xs"
                        value={fsAlignment}
                        onChange={(val: any) => setFsAlignment(val as ExtensionAlignment)}
                        data={[
                          { label: '頂部 / 靠左 (Start)', value: ExtensionAlignment.Start },
                          { label: '居中 (Center)', value: ExtensionAlignment.Center },
                          { label: '底部 / 靠右 (End)', value: ExtensionAlignment.End },
                          { label: '拉伸撐滿 (Stretch)', value: ExtensionAlignment.Stretch },
                        ]}
                      />
                    </div>
                  )}
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            取消
          </Button>
          <Button onClick={handleSave}>
            確認
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
